import { useManagementForceCancelUnitActivation } from '@apps-common/apis/management/unit-activation'
import { useActionUnitActivation } from '@apps-common/apis/portal/unit-activations/action-unit-activation'
import { useArchiveUnitActivation } from '@apps-common/apis/portal/unit-activations/archive-unit-activation-by-id'
import { useCancelUnitActivation } from '@apps-common/apis/portal/unit-activations/cancel-unit-activation-by-id'
import { useDeleteUnitActivation } from '@apps-common/apis/portal/unit-activations/delete-unit-activation-by-id'
import { ButtonRoundSm } from '@apps-common/components/elements'
import { ConfirmModal, ConfirmModalAction, ModalButton } from '@apps-common/components/modals'
import { commonIcons } from '@apps-common/config/icons'
import { UnitActivationAction } from '@apps-common/config/unit-activation'
import { useNavigation } from '@apps-common/hooks/useNavigation'
import { useUnitActivationStatusOptions } from '@apps-common/hooks/useUnitActivationStatusOptions'
import { AUTH_CLAIMS, Authorization } from '@apps-common/lib'
import { modalSlideUpEnterAnimation, modalSlideUpLeaveAnimation } from '@apps-common/lib/modal-animations'
import { IActivationServiceRequestModel } from '@apps-common/types/portal/service-request'
import { ROUTE_EDIT_UNIT_ACTIVATION, ROUTE_UNIT_ACTIVATIONS } from '@apps-radionet-portal-root/src/config/private-routes'
import classNames from 'classnames'
import { ActionButton } from '../../../components/UnitActivationActionButton'
import { invalidateActivationQueries } from '../../../lib/invalidateActivationQueries'
import { UnitActivationOptionsModal } from '../../../modals'

const ACTIVATION_CARD_ACTION_EXTRA_STYLES =
  'w-auto !h-[24px] px-2 gap-[5px] disabled:!cursor-not-allowed disabled:!bg-surface !text-[0.8rem] text-nowrap !w-auto'
interface IProps {
  displayMode?: 'mobile' | 'desktop'
  activation: IActivationServiceRequestModel | undefined
}

export const UnitActivationDetailsOptions: React.FC<IProps> = ({ displayMode, activation }) => {
  const { unitActivationRequestId, status, archived } = activation || {}

  const { navigateForward, navigateReplace } = useNavigation()

  const { isProcessing, showRetry, showActivate, showDelete, showArchive, showEdit, canCancel } = useUnitActivationStatusOptions(
    status!,
    archived!
  )
  const { mutate: actionActivation, isPending: actionActivationIsPending } = useActionUnitActivation(true, (data) =>
    invalidateActivationQueries(data.unitActivationRequestId)
  )
  const { mutate: archiveActivation, isPending: archiveActivationIsPending } = useArchiveUnitActivation(true, (data) =>
    invalidateActivationQueries()
  )
  const { mutate: deleteActivation, isPending: deleteActivationIsPending } = useDeleteUnitActivation(true, (data) => {
    invalidateActivationQueries(data.unitActivationRequestId)
    navigateReplace({ routeUrl: ROUTE_UNIT_ACTIVATIONS.buildPath(), route: ROUTE_UNIT_ACTIVATIONS })
  })
  const { mutate: cancelActivation, isPending: cancelActivationIsPending } = useCancelUnitActivation(true, (data) =>
    invalidateActivationQueries(data.unitActivationRequestId)
  )
  const { mutate: forceCancelActivation, isPending: forceCancelIsPending } = useManagementForceCancelUnitActivation(true, (data) =>
    invalidateActivationQueries(data.unitActivationRequestId)
  )

  const onOptionSelected = (action: string) => {
    if (action === UnitActivationAction.forceCancel) {
      forceCancelActivation({ id: unitActivationRequestId! })
    } else if (action === UnitActivationAction.activateNow) {
      actionActivation({ model: activation as any, action })
    } else if (action === UnitActivationAction.powerUp) {
      actionActivation({ model: activation as any, action })
    } else if (action === UnitActivationAction.edit) {
      navigateForward({
        routeUrl: ROUTE_EDIT_UNIT_ACTIVATION.buildPath(unitActivationRequestId),
        route: ROUTE_EDIT_UNIT_ACTIVATION
      })
    } else if (action === UnitActivationAction.delete) {
      deleteActivation({ id: unitActivationRequestId || null })
    } else if (action === UnitActivationAction.archive) {
      archiveActivation({ id: unitActivationRequestId || null })
    } else if (action === UnitActivationAction.cancel) {
      cancelActivation({ id: unitActivationRequestId || null })
    }
  }

  const loading =
    !activation || actionActivationIsPending || archiveActivationIsPending || deleteActivationIsPending || cancelActivationIsPending

  return (
    <div>
      {displayMode === 'mobile' && (
        <ModalButton
          component={UnitActivationOptionsModal}
          disabled={false}
          classes='block sm:hidden w-full'
          componentProps={{
            activation
          }}
          enterAnimation={modalSlideUpEnterAnimation}
          leaveAnimation={modalSlideUpLeaveAnimation}
          confirmCallback={(data) => onOptionSelected(data?.action)}
        >
          <ButtonRoundSm type='div' disabled={loading} className='action-success'>
            <commonIcons.settings />
          </ButtonRoundSm>
        </ModalButton>
      )}

      {displayMode !== 'mobile' && (
        <div className='hidden sm:flex flex-col gap-2'>
          <p className='text-text-muted/70 text-[0.75em] text-left'>Some options may be unavailable based on activation status.</p>
          <div className='flex flex-wrap items-start justify-start gap-[5px] max-w-[800px] mb-4'>
            <Authorization roles={AUTH_CLAIMS.managementAdmin.roles} scopes={AUTH_CLAIMS.managementAdmin.scopes}>
              <ModalButton
                component={ConfirmModal}
                disabled={!isProcessing}
                classes=''
                componentProps={{
                  text: 'Force Cancel',
                  description: `Confirm you would like to FORCE cancel this device activation? 
                                  
                                  Please note that if the activation is in progress, it may still complete the activation and potentially update this activation afterwards. 
                                  Please only use this feature if you believe the activation is stuck.`,
                  action: ConfirmModalAction.Error
                }}
                confirmCallback={() => forceCancelActivation({ id: unitActivationRequestId! })}
              >
                <ActionButton
                  type='div'
                  action={UnitActivationAction.terminate}
                  label='Force Cancel'
                  disabled={!isProcessing}
                  className={classNames('action-delete', ACTIVATION_CARD_ACTION_EXTRA_STYLES)}
                />
              </ModalButton>
            </Authorization>
            <ActionButton
              type='button'
              action={UnitActivationAction.activateNow}
              disabled={(!showActivate && !showRetry) || loading}
              label={showRetry ? 'Retry now' : 'Activate now'}
              className={classNames('action-activate-now', ACTIVATION_CARD_ACTION_EXTRA_STYLES)}
              callback={() => actionActivation({ model: activation!, action: UnitActivationAction.activateNow })}
            />
            <ActionButton
              type='button'
              action={UnitActivationAction.powerUp}
              disabled={(!showActivate && !showRetry) || loading}
              label={showRetry ? 'Retry on power up' : 'Activate on power up'}
              className={classNames('action-power-up', ACTIVATION_CARD_ACTION_EXTRA_STYLES)}
              callback={() => actionActivation({ model: activation!, action: UnitActivationAction.powerUp })}
            />

            <ActionButton
              type='button'
              action={UnitActivationAction.edit}
              disabled={!showEdit || loading}
              label='Edit'
              className={classNames('action-edit', ACTIVATION_CARD_ACTION_EXTRA_STYLES)}
              callback={() => {
                navigateForward({
                  routeUrl: ROUTE_EDIT_UNIT_ACTIVATION.buildPath(unitActivationRequestId),
                  route: ROUTE_EDIT_UNIT_ACTIVATION
                })
              }}
            />

            <ModalButton
              component={ConfirmModal}
              disabled={!showDelete || loading}
              classes=''
              componentProps={{
                text: 'Delete',
                description: 'Confirm you would like to delete this device activation?',
                action: ConfirmModalAction.Error
              }}
              confirmCallback={() => deleteActivation({ id: unitActivationRequestId! })}
            >
              <ActionButton
                type='div'
                action={UnitActivationAction.delete}
                disabled={!showDelete || loading}
                label='Delete'
                className={classNames('action-delete', ACTIVATION_CARD_ACTION_EXTRA_STYLES)}
              />
            </ModalButton>

            <ModalButton
              component={ConfirmModal}
              disabled={!showArchive || loading}
              classes=''
              componentProps={{
                text: 'Archive',
                description: 'Confirm you would like to archive this device activation?',
                action: ConfirmModalAction.Pending
              }}
              confirmCallback={() => archiveActivation({ id: unitActivationRequestId! })}
            >
              <ActionButton
                type='div'
                action={UnitActivationAction.archive}
                disabled={!showArchive || loading}
                label='Archive'
                className={classNames('action-archive', ACTIVATION_CARD_ACTION_EXTRA_STYLES)}
              />
            </ModalButton>

            <ModalButton
              component={ConfirmModal}
              disabled={!canCancel || loading}
              classes=''
              componentProps={{
                text: 'Cancel',
                description: 'Confirm you would like to cancel this device activation?',
                action: ConfirmModalAction.Warning
              }}
              confirmCallback={() => cancelActivation({ id: unitActivationRequestId! })}
            >
              <ActionButton
                type='div'
                action={UnitActivationAction.cancel}
                disabled={!canCancel || loading}
                label='Cancel'
                className={classNames('action-cancel', ACTIVATION_CARD_ACTION_EXTRA_STYLES)}
              />
            </ModalButton>
          </div>
        </div>
      )}
    </div>
  )
}
